import { useEffect, useRef } from "react";

/**
 * Lightweight per-page SEO helper — updates <title> and description meta.
 * Also fires an analytics page_view via /api/analytics/track (best effort).
 *
 * Regole di tracciamento:
 * - amministratori autenticati (token in localStorage) NON vengono tracciati;
 * - una sola page_view per path per montaggio pagina (niente duplicati da re-render);
 * - ogni evento porta un id di sessione anonimo in sessionStorage (muore con la scheda),
 *   che consente di misurare le sessioni senza identificare il visitatore;
 * - la sorgente di traffico (utm_source → referrer → diretto) è rilevata all'arrivo
 *   e resta valida per tutta la sessione (first-touch).
 */
function getSessionId() {
  try {
    let sid = sessionStorage.getItem("gm_analytics_sid");
    if (!sid) {
      sid = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("gm_analytics_sid", sid);
    }
    return sid;
  } catch { return ""; }
}

function isAdminSession() {
  try { return !!localStorage.getItem("gm_admin_token"); } catch { return false; }
}

const SOURCE_MAP = [
  ["google", "Google"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["l.facebook", "Facebook"],
  ["pinterest", "Pinterest"], ["whatsapp", "WhatsApp"], ["tiktok", "TikTok"], ["bing", "Bing"],
  ["t.co", "X (Twitter)"], ["twitter", "X (Twitter)"], ["linkedin", "LinkedIn"],
  ["newsletter", "Email / Newsletter"], ["email", "Email / Newsletter"], ["youtube", "YouTube"],
];

function classifySourceValue(v) {
  if (!v) return null;
  const low = v.toLowerCase();
  for (const [token, label] of SOURCE_MAP) {
    if (low.includes(token)) return label;
  }
  return null;
}

function getSource() {
  try {
    const cached = sessionStorage.getItem("gm_analytics_src");
    if (cached) return cached;
  } catch { /* storage non disponibile */ }
  let src = null;
  try { src = classifySourceValue(new URLSearchParams(window.location.search).get("utm_source")); } catch { /* noop */ }
  let host = "";
  try { host = document.referrer ? new URL(document.referrer).hostname : ""; } catch { /* noop */ }
  if (!src) src = classifySourceValue(host);
  if (!src) src = host && host !== window.location.hostname ? `Altro: ${host}` : "Diretto / nessun referrer";
  try { sessionStorage.setItem("gm_analytics_src", src); } catch { /* noop */ }
  return src;
}

export default function useSEO({ title, description, path, jsonLd } = {}) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";
  const trackedPath = useRef(null);
  useEffect(() => {
    if (title) document.title = `${title} · Giovanna Matino Interior`;
    if (description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", description);
    }
    // og:title
    let og = document.querySelector('meta[property="og:title"]');
    if (og && title) og.setAttribute("content", title);
    let ogd = document.querySelector('meta[property="og:description"]');
    if (ogd && description) ogd.setAttribute("content", description);
    // JSON-LD structured data
    if (jsonLdStr) {
      let ld = document.getElementById("seo-jsonld");
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "seo-jsonld";
        document.head.appendChild(ld);
      }
      ld.textContent = jsonLdStr;
    }
    // canonical + og:url dinamici: il sito migrato non eredita mai il vecchio dominio
    const canonicalUrl = window.location.origin + (path || window.location.pathname);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", canonicalUrl);
    let ogu = document.querySelector('meta[property="og:url"]');
    if (!ogu) {
      ogu = document.createElement("meta");
      ogu.setAttribute("property", "og:url");
      document.head.appendChild(ogu);
    }
    ogu.setAttribute("content", canonicalUrl);
    // best-effort analytics (no imports required, avoids circular deps)
    const currentPath = path || window.location.pathname;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    if (BACKEND_URL && !isAdminSession() && trackedPath.current !== currentPath) {
      trackedPath.current = currentPath;
      fetch(`${BACKEND_URL}/api/analytics/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "page_view", path: currentPath, meta: { sid: getSessionId(), src: getSource() } }),
      }).catch(() => {});
    }
    return () => {
      if (jsonLdStr) document.getElementById("seo-jsonld")?.remove();
    };
  }, [title, description, path, jsonLdStr]);
}
